'use client'

import Script from 'next/script'
import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useConsentReady } from '@/hooks/useConsentReady'

declare global {
  interface Window {
    OBR?: any
    __OBR_LOADED__?: boolean
  }
}

export const Outbrain = () => {
  const consentReady = useConsentReady()
  const pathname = usePathname()

  const [shouldLoad, setShouldLoad] = useState(false)
  const scriptReady = useRef(false)
  const refreshTimer = useRef<number | null>(null)
  const armedRef = useRef(false)

  const onIdle = (cb: () => void, timeout = 1500) => {
    const ric: any = (window as any).requestIdleCallback
    if (typeof ric === 'function') ric(cb, { timeout })
    else setTimeout(cb, timeout)
  }

  const deferUntilLCP = (cb: () => void) => {
    let done = false
    const finish = () => {
      if (!done) {
        done = true
        cb()
      }
    }

    // Prefer after-LCP to keep LCP clean.
    try {
      const po = new PerformanceObserver((list) => {
        if (list.getEntries().length) {
          setTimeout(finish, 800) // small settle buffer
          po.disconnect()
        }
      })
      po.observe({ type: 'largest-contentful-paint', buffered: true })
      setTimeout(finish, 4000)
    } catch {
      onIdle(finish, 2500)
    }
  }

  // Debounced SDK refresh (SPA safe)
  const requestRefresh = () => {
    if (!window.__OBR_LOADED__ || !window.OBR) return
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current)
    refreshTimer.current = window.setTimeout(() => {
      try {
        window.OBR.extern?.reload?.()
      } catch {
        console.warn('Outbrain refresh failed')
      }
      refreshTimer.current = null
    }, 200)
  }

  useEffect(() => {
    if (
      !consentReady ||
      window.__OBR_LOADED__ ||
      shouldLoad ||
      armedRef.current
    )
      return
    armedRef.current = true

    deferUntilLCP(() => {
      const armOnce = () => setShouldLoad(true)

      const firstSlot =
        document.querySelector<HTMLElement>('.OUTBRAIN') ??
        document.querySelector<HTMLElement>('[data-ob-identifier]')

      if (firstSlot) {
        const io = new IntersectionObserver(
          (ents) => {
            if (ents.some((e) => e.isIntersecting)) {
              armOnce()
              io.disconnect()
              removeInputListeners()
            }
          },
          { rootMargin: '600px', threshold: 0.01 },
        )
        io.observe(firstSlot)
        onIdle(armOnce, 3500)
      } else {
        onIdle(armOnce, 3500)
      }

      const onAnyInput = () => {
        armOnce()
        removeInputListeners()
      }
      const removeInputListeners = () => {
        window.removeEventListener(
          'scroll',
          onAnyInput as any,
          { capture: false } as any,
        )
        window.removeEventListener('pointerdown', onAnyInput)
        window.removeEventListener('keydown', onAnyInput)
      }
      window.addEventListener('scroll', onAnyInput as any, { passive: true })
      window.addEventListener('pointerdown', onAnyInput)
      window.addEventListener('keydown', onAnyInput)
    })
  }, [consentReady, shouldLoad])

  useEffect(() => {
    requestRefresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    if (scriptReady.current) requestRefresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptReady.current])

  useEffect(() => {
    const dns = document.createElement('link')
    dns.rel = 'dns-prefetch'
    dns.href = 'https://widgets.outbrain.com'
    const pre = document.createElement('link')
    pre.rel = 'preconnect'
    pre.href = 'https://widgets.outbrain.com'
    pre.crossOrigin = ''
    document.head.appendChild(dns)
    document.head.appendChild(pre)
    return () => {
      try {
        document.head.removeChild(dns)
      } catch {
        console.warn('failed to remove dns-prefetch')
      }
      try {
        document.head.removeChild(pre)
      } catch {
        console.warn('failed to remove preconnect')
      }
    }
  }, [])

  if (!consentReady || !shouldLoad) return null

  return (
    <Script
      id="outbrain-sdk"
      src="https://widgets.outbrain.com/outbrain.js"
      strategy="lazyOnload"
      async
      onLoad={() => {
        window.__OBR_LOADED__ = true
        scriptReady.current = true
        requestRefresh()
      }}
      onError={() => {
        window.__OBR_LOADED__ = false
        scriptReady.current = false
      }}
    />
  )
}
